import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { storageService } from './storageService';

const PLACEHOLDER_URL = 'https://placeholder.supabase.co';
const PLACEHOLDER_KEY = 'placeholder-key';

const supabase_url = process.env.REACT_APP_SUPABASE_URL || '';
const supabase_anon_key = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

const is_configured = Boolean(supabase_url && supabase_anon_key);

if (!is_configured) {
  console.warn(
    'Supabase env vars missing (REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_ANON_KEY). ' +
    'Supabase features will not work until these are set.'
  );
}

export const supabase: SupabaseClient = createClient(
  supabase_url || PLACEHOLDER_URL,
  supabase_anon_key || PLACEHOLDER_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  },
);

export const is_supabase_configured = is_configured;

const PIN_MEMBER_SESSION_KEY = 'ftm_member_pin';

/** After PIN login, JWT lives in storage; pair with parked guardian session OR this tab flag (see setPinMemberSession). */
export function isMemberMode(): boolean {
  const token = storageService.getAuthToken();
  if (!token) return false;
  if (storageService.getGuardianSession()) return true;
  try {
    if (typeof window !== 'undefined' && window.sessionStorage.getItem(PIN_MEMBER_SESSION_KEY) === '1') return true;
  } catch {
    /* ignore */
  }
  return false;
}

export function setPinMemberSession(): void {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(PIN_MEMBER_SESSION_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function clearPinMemberSession(): void {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.removeItem(PIN_MEMBER_SESSION_KEY);
  } catch {
    /* ignore */
  }
  // Drop the member's real Supabase session from this device (local only — other devices keep theirs).
  if (_memberSupabase) {
    _memberSupabase.auth.signOut({ scope: 'local' }).catch(() => {});
  }
}

/**
 * PIN members now get a real Supabase session (docs/design/pin-member-auth.md). It lives in its
 * own storage slot so the guardian's parked session is never overwritten, and it renews itself
 * (autoRefreshToken) — the old self-signed member token expired after a hard hour.
 */
const MEMBER_SESSION_STORAGE_KEY = 'antnest-member-session';
let _memberSupabase: SupabaseClient | null = null;

export function getMemberSupabase(): SupabaseClient {
  if (!_memberSupabase) {
    _memberSupabase = createClient(supabase_url || PLACEHOLDER_URL, supabase_anon_key || PLACEHOLDER_KEY, {
      auth: {
        storageKey: MEMBER_SESSION_STORAGE_KEY,
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    });
    // Keep the stored member token in sync with refreshes so isMemberMode() and the existing
    // token plumbing keep working.
    _memberSupabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token && isMemberMode()) storageService.setAuthToken(session.access_token);
    });
  }
  return _memberSupabase;
}


/** Tab flag for PIN member session (same key as isMemberMode pin branch). */
export function hasPinMemberSessionFlag(): boolean {
  try {
    return typeof window !== 'undefined' && window.sessionStorage.getItem(PIN_MEMBER_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Supabase client for API calls: the PIN member's own session client in member mode, otherwise the
 * guardian's main client. (The old variant that attached a self-signed member token was removed in
 * step 3 of docs/design/pin-member-auth.md — the server no longer accepts those tokens.)
 */
export function getSupabaseForApi(): SupabaseClient {
  return isMemberMode() ? getMemberSupabase() : supabase;
}
